import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

export default function Nav({ light = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 32);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkCls = ({ isActive }) =>
        `text-[13px] tracking-[0.14em] uppercase transition-colors ${isActive ? "text-accent" : "hover:text-accent"}`;

    return (
        <nav
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-background/80 backdrop-blur-2xl border-b hairline"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                    aria-label="Access Credit Repair — Home"
                >
                    <span className="w-6 h-6 border-2 border-foreground grid place-items-center relative">
                        <span className="w-2 h-2 bg-foreground" />
                    </span>
                    <span className="font-editorial text-xl md:text-[22px] tracking-tight">
                        Access<span className="italic text-accent"> / </span>Credit Repair
                    </span>
                </Link>

                <div className="hidden md:flex items-center gap-10">
                    <NavLink to="/#services" className={linkCls} data-testid="nav-services">
                        Services
                    </NavLink>
                    <NavLink to="/#process" className={linkCls} data-testid="nav-process">
                        Process
                    </NavLink>
                    <NavLink to="/#results" className={linkCls} data-testid="nav-results">
                        Results
                    </NavLink>
                    <NavLink to="/simulator" className={linkCls} data-testid="nav-simulator">
                        Simulator
                    </NavLink>
                    <NavLink to="/flyer" className={linkCls} data-testid="nav-flyer">
                        Flyer
                    </NavLink>
                    {user && user !== false ? (
                        <div className="flex items-center gap-4">
                            {user.role === "admin" && (
                                <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
                                    Admin
                                </NavLink>
                            )}
                            <NavLink to="/portal" className={linkCls} data-testid="nav-portal">
                                Portal
                            </NavLink>
                            <button
                                data-testid="nav-logout"
                                onClick={async () => {
                                    await logout();
                                    navigate("/");
                                }}
                                className="text-[13px] tracking-[0.14em] uppercase hover:text-accent"
                            >
                                Logout
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-4">
                            <NavLink to="/login" className={linkCls} data-testid="nav-login">
                                Login
                            </NavLink>
                            <Link
                                to="/enroll"
                                data-testid="nav-cta"
                                className="text-[13px] tracking-[0.14em] uppercase bg-foreground text-background px-5 py-3 hover:bg-accent"
                            >
                                Get Started · $500
                            </Link>
                        </div>
                    )}
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t hairline bg-background">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        <Link to="/#services" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                            Services
                        </Link>
                        <Link to="/#process" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                            Process
                        </Link>
                        <Link to="/#results" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                            Results
                        </Link>
                        <Link to="/simulator" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                            Simulator
                        </Link>
                        <Link to="/flyer" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                            Flyer
                        </Link>
                        {user && user !== false ? (
                            <>
                                {user.role === "admin" && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                                        Admin
                                    </Link>
                                )}
                                <Link to="/portal" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                                    Portal
                                </Link>
                                {user.role === "admin" && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                                        Admin
                                    </Link>
                                )}
                                <button
                                    onClick={async () => {
                                        await logout();
                                        setOpen(false);
                                        navigate("/");
                                    }}
                                    className="uppercase text-sm tracking-widest text-left"
                                    data-testid="nav-mobile-logout"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest">
                                    Login
                                </Link>
                                <Link to="/enroll" onClick={() => setOpen(false)} className="uppercase text-sm tracking-widest bg-foreground text-background px-5 py-3 inline-block w-fit">
                                    Get Started · $500
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
}
